import { Injectable } from '@angular/core';
import { HttpMethodType } from '@app/app.constant';
import { HttpCallService } from '@app/core/services/http-call.service';
import {
  IGetSessionShareAuditRequest,
  IGetSessionShareAuditResponse,
  IGetSessionShareGrantsRequest,
  ISaveSessionShareGrantRequest,
  ISessionShareGrantInfo,
  IGetShareTargetUsersRequest,
  ISharingAvailabilityInfo,
  ISharingDefaultsInfo,
  IUpdateSessionShareExclusionsRequest
} from '@app/models/session-share/session-share.model';
import { Observable, Subject } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';

const HttpServiceMethods = {
  GetSharingEnabled: 'SessionShare/GetSharingEnabled',
  GetShareTargetUsers: 'SessionShare/GetShareTargetUsers',
  GetGrantsForScope: 'SessionShare/GetGrantsForScope',
  CreateGrant: 'SessionShare/CreateGrant',
  RevokeGrant: 'SessionShare/RevokeGrant',
  UpdateExclusions: 'SessionShare/UpdateExclusions',
  GetAudit: 'SessionShare/GetAudit'
};

@Injectable({
  providedIn: 'root'
})
export class SessionShareService {
  constructor(private httpService: HttpCallService) {}

  /**
   * Fires when the session-sharing master switch may have changed (saved on Update Project).
   * The sidebar listens so the Sharing menu item appears/disappears on save rather than waiting
   * for the next page refresh.
   */
  private readonly sharingSwitchChanged = new Subject<void>();

  public get sharingSwitchChanged$(): Observable<void> {
    return this.sharingSwitchChanged.asObservable();
  }

  public notifySharingSwitchChanged(): void {
    this.sharingSwitchChanged.next();
  }

  // The one GetSharingEnabled request currently on the wire, if any. Callers that arrive while it
  // is pending share its answer instead of firing their own; cleared the moment it settles.
  private sharingEnabledInFlight: Observable<ISharingAvailabilityInfo> | null = null;

  /**
   * Project master switch, read fresh per page load. The sharing entry points gate on this rather
   * than on a value carried in the sign-in token, so switching the feature off hides them on the
   * next page view instead of only after the user signs in again.
   *
   * Deduplicated while in flight, NOT cached: the sidebar and the profile page both ask for this
   * on the same page load, so they share one request, but the next page load asks again and
   * still sees a switch that was flipped in between. Deliberately no time-based cache - a
   * licence or project switch would otherwise show the previous project's answer for a while.
   */
  public getSharingEnabled(): Observable<ISharingAvailabilityInfo> {
    if (!this.sharingEnabledInFlight) {
      this.sharingEnabledInFlight = this.httpService
        .callService(HttpMethodType.GET, HttpServiceMethods.GetSharingEnabled)
        .pipe(
          finalize(() => (this.sharingEnabledInFlight = null)),
          shareReplay(1)
        );
    }
    return this.sharingEnabledInFlight;
  }

  // getSharingDefaults / saveSharingDefaults were retired when the sharing options moved onto
  // Update Project — the project save is now the single write path for sharing configuration.

  /**
   * People who can be shared with for this scope — scoped server-side to the relevant team.
   * Includes the current user (isCurrentUser), who is offered so a manager can grant themselves
   * note access on a case they need to review. Audited like any other share.
   */
  public getShareTargetUsers(
    request: IGetShareTargetUsersRequest
  ): Observable<{ userGuid: string; fullName: string; isCurrentUser?: boolean }[]> {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.GetShareTargetUsers, request);
  }

  public getGrantsForScope(request: IGetSessionShareGrantsRequest): Observable<ISessionShareGrantInfo[]> {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.GetGrantsForScope, request);
  }

  public createGrant(request: ISaveSessionShareGrantRequest): Observable<ISessionShareGrantInfo> {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.CreateGrant, request);
  }

  public revokeGrant(shareGrantGuid: string): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.RevokeGrant, { shareGrantGuid });
  }

  public updateExclusions(request: IUpdateSessionShareExclusionsRequest): Observable<boolean> {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.UpdateExclusions, request);
  }

  // setVisibilityModel retired: there is no per-session visibility baseline any more.

  public getAudit(request: IGetSessionShareAuditRequest): Observable<IGetSessionShareAuditResponse> {
    return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.GetAudit, request);
  }
}
